import { useRef, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import MacroBar from "../components/MacroBar";
import { btn, inp } from "../styles/shared";
import { MEALS } from "../constants/config";
import { calcNavyBF, getCurrentBodyComposition, calcBMR, calcMaintenance } from "../utils/calculations";
import { getTodayDate, fmtDate } from "../utils/formatting";
import { readImageMetadata } from "../utils/imageMetadata";

const mColors = { calories: "#fb923c", protein: "#60a5fa", carbs: "#4ade80", fat: "#fbbf24", fiber: "#c084fc" };

export default function Dashboard({
  logs, saveLogs,
  macroGoals, saveMacroGoals,
  fitnessGoals, saveFitnessGoals,
  workouts, saveWorkouts,
  weightLog, healthConnect, todayWeighIn, setTodayWeighIn,
  saveWeightEntry,
  editFitnessGoals, setEditFitnessGoals,
  editMacroGoals, setEditMacroGoals,
  visibleGoalCards, setVisibleGoalCards,
  getDayTotals, showToast, addLog,
  setView, setActiveMeal,
  logDate,
  sortedLogs,
  chartMetric, setChartMetric,
  timeRange, setTimeRange,
  todayDay,
}) {
  const today = getTodayDate();
  const todayTotals = getDayTotals(today);

  // ── Chart data ──────────────────────────────────────────────────────────────
  const cutoffDays = { week: 7, month: 30, "3months": 90 };
  const now = new Date();
  const chartLogs = sortedLogs
    .filter(l => {
      if (timeRange === "all") return true;
      return (now - new Date(l.date)) / 86400000 <= cutoffDays[timeRange];
    })
    .map(l => { const t = getDayTotals(l.date); return t ? { date: fmtDate(l.date), ...t } : null; })
    .filter(Boolean);

  const photoRef = useRef();
  const [weighInPhoto, setWeighInPhoto] = useState(null); // compressed base64
  const [weighInPhotoMetadata, setWeighInPhotoMetadata] = useState(null);

  async function handlePhotoCapture(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    try {
      const metadata = await readImageMetadata(file, today);
      const compressed = await compressPhoto(file, 1400, 0.82);
      setWeighInPhoto(compressed);
      setWeighInPhotoMetadata(metadata);
      if (metadata.metadataWarnings?.some(warning => warning.includes("differs"))) {
        showToast("Photo date differs from today's weigh-in");
      }
    } catch {
      showToast("Could not read photo");
    }
  }

  function compressPhoto(file, maxPx, quality) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(maxPx / img.width, maxPx / img.height, 1);
        const w = Math.round(img.width  * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  // ── Handlers ────────────────────────────────────────────────────────────────
  async function logWeight() {
    const kg = parseFloat(todayWeighIn);
    if (!kg || kg < 30 || kg > 250) return;
    if (!weighInPhoto) { showToast("Take a photo first — it's required for weigh-in"); return; }
    const saved = await saveWeightEntry(kg, weighInPhoto, weighInPhotoMetadata);
    if (!saved) return;
    setTodayWeighIn("");
    setWeighInPhoto(null);
    setWeighInPhotoMetadata(null);
    showToast("Weight and photo saved!");
  }

  function openMeal(meal) {
    const logged = logs.find(l => l.date === logDate)?.meals?.[meal];
    setActiveMeal({
      meal,
      isEdit: !!(logged?.ingredients?.length),
      ingredients: logged?.ingredients || [{ name: "", amount: 100, unit: "g" }],
      notes: logged?.notes || "",
    });
  }

  return (
<div style={{ display:"flex", flexDirection:"column", gap:22 }}>
  {(()=>{
    const todayEntry = weightLog.find(w=>w.date===today);
    const last7 = [...weightLog].filter(w=>(new Date()-new Date(w.date))/86400000<=7).sort((a,b)=>a.date.localeCompare(b.date));
    const prevEntry = last7.filter(w=>w.date!==today).slice(-1)[0];
    const delta = todayEntry && prevEntry ? (todayEntry.weight - prevEntry.weight).toFixed(2) : null;
    const todayWoCount = workouts.filter(w=>w.date===today).length;

    return (
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:14 }}>
        {/* Title */}
        <div>
          <h1 style={{ fontSize:26,fontWeight:600,margin:0,letterSpacing:-0.5 }}>Dashboard</h1>
          <p style={{ color:"#999",margin:"4px 0 0",fontSize:14 }}>{todayDay} · {new Date().toLocaleDateString("en-ZA",{month:"long",day:"numeric",year:"numeric"})}</p>
        </div>
        {/* Quick actions */}
        <div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" }}>
          {/* Weigh-in pill */}
          <div style={{ display:"flex",alignItems:"center",gap:8,background:"linear-gradient(135deg,#0f0b1e,#0a0817)",border:"1px solid rgba(139,92,246,0.35)",borderRadius:12,padding:"8px 14px",boxShadow:"0 2px 10px rgba(0,0,0,0.4)" }}>
            <div style={{ display:"flex",flexDirection:"column",gap:1 }}>
              <span style={{ fontSize:9,fontFamily:"'Space Mono',monospace",color:"#9a8ab0",textTransform:"uppercase",letterSpacing:1 }}>Daily Weigh-in</span>
              <span style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#fff",fontWeight:600 }}>
                {todayEntry ? todayEntry.weight+"kg" : "—"}
                {delta!==null && (
                  <span style={{ marginLeft:6,fontSize:10,fontWeight:400,color:parseFloat(delta)<0?"#4ade80":parseFloat(delta)>0?"#f87171":"#aaa" }}>
                    {parseFloat(delta)>0?"+":""}{delta}kg
                  </span>
                )}
              </span>
            </div>
            {last7.length>1 && (
              <div style={{ display:"flex",alignItems:"flex-end",gap:2,height:18,margin:"0 4px" }}>
                {last7.map((e,i)=>{
                  const vals=last7.map(x=>x.weight); const mn=Math.min(...vals)-0.2; const mx=Math.max(...vals)+0.2;
                  const h=Math.max(3,Math.round(((e.weight-mn)/(mx-mn||1))*14));
                  return <div key={i} style={{ width:4,height:h,borderRadius:1,background:e.date===today?"#a78bfa":"rgba(167,139,250,0.3)" }}/>;
                })}
              </div>
            )}
            <input type="number" step="0.05" placeholder="00.00"
              value={todayWeighIn}
              onChange={e=>setTodayWeighIn(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&logWeight()}
              style={{...inp,width:82,fontSize:13,fontFamily:"'Space Mono',monospace",textAlign:"center",color:"#fff",padding:"5px 8px",borderColor:"rgba(139,92,246,0.3)",background:"rgba(139,92,246,0.1)",margin:0}}/>
            {/* Photo capture — required before save */}
            <input ref={photoRef} type="file" accept="image/*" capture="environment" style={{ display:"none" }} onChange={handlePhotoCapture}/>
            {weighInPhoto ? (
              <img src={weighInPhoto} alt="weigh-in" onClick={()=>photoRef.current?.click()}
                style={{ width:32,height:32,borderRadius:6,objectFit:"cover",border:"2px solid #a78bfa",cursor:"pointer",flexShrink:0 }}/>
            ) : (
              <button onClick={()=>photoRef.current?.click()} title="Required: take a photo"
                style={{ ...btn,fontSize:11,padding:"5px 9px",background:"rgba(139,92,246,0.08)",color:"rgba(167,139,250,0.6)",border:"1px solid rgba(139,92,246,0.2)",flexShrink:0 }}>📷</button>
            )}
            <button onClick={logWeight} disabled={!weighInPhoto}
              style={{ ...btn,fontSize:10,padding:"5px 11px",background:weighInPhoto?"rgba(139,92,246,0.25)":"rgba(139,92,246,0.08)",color:weighInPhoto?"#fff":"#666",border:"1px solid " + (weighInPhoto?"rgba(139,92,246,0.4)":"rgba(139,92,246,0.15)"),fontWeight:600,cursor:weighInPhoto?"pointer":"not-allowed" }}>Log</button>
          </div>
          {/* Log Workout pill */}
          <button onClick={()=>setView("Fitness")} style={{ ...btn,display:"flex",alignItems:"center",gap:8,padding:"9px 16px",fontSize:12,background:todayWoCount?"rgba(52,211,153,0.15)":"linear-gradient(135deg,#061a14,#041108)",color:"#fff",border:"1px solid " + (todayWoCount?"rgba(52,211,153,0.4)":"rgba(52,211,153,0.25)"),borderRadius:12,boxShadow:"0 2px 10px rgba(0,0,0,0.3)",fontWeight:600 }}>
            <span style={{ fontSize:13 }}>🏋️</span>
            <span>{todayWoCount?"✓ " + todayWoCount + " Session" + (todayWoCount>1?"s":""):"Log Workout"}</span>
          </button>
        </div>
      </div>
    );
  })()}



  {/* ── Daily Deficit / Surplus Widget ── */}
  {(()=>{
    // 7-day rolling average weight — smooths out daily water fluctuations
    const recentWeights = [...weightLog]
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 7)
      .map(e => e.weight)
      .filter(Boolean);
    const avgWeight = recentWeights.length
      ? recentWeights.reduce((s, v) => s + v, 0) / recentWeights.length
      : fitnessGoals.weightCurrent;
    const w = avgWeight || fitnessGoals.weightCurrent;
    const h = fitnessGoals.height;
    const a = fitnessGoals.age;
    const todayWos = workouts.filter(wo => wo.date === today);
    const woBurn   = todayWos.reduce((s, w) => s + (w.caloriesBurned || 0), 0);
    const maint   = calcMaintenance(w, h, a);
    const eaten   = todayTotals?.calories || 0;
    if (!maint) return null;
    const totalBurn = maint + woBurn;
    const net = totalBurn - eaten;
    const isDeficit = net >= 0;
    const deficitBorderColor = isDeficit ? "rgba(74,222,128,0.28)" : "rgba(248,113,113,0.28)";
    const pct = Math.min(100, Math.round((eaten / totalBurn) * 100));
    return (
      <div style={{ background:"linear-gradient(135deg,#0a0a1a,#080812)",border:"1px solid "+deficitBorderColor,borderRadius:14,padding:"14px 16px",boxShadow:"0 2px 10px rgba(0,0,0,0.4)" }}>
        <div style={{ fontSize:9,fontFamily:"'Space Mono',monospace",color:"#999",textTransform:"uppercase",letterSpacing:1.2,marginBottom:12 }}>Daily Balance</div>
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr"+(woBurn>0?" 1fr":""),gap:10,marginBottom:12 }}>
          <div style={{ background:"rgba(196,181,253,0.07)",borderRadius:10,padding:"10px 12px" }}>
            <div style={{ fontSize:10,color:"#888",marginBottom:4 }}>Maintenance</div>
            <div style={{ fontSize:17,fontWeight:700,fontFamily:"'Space Mono',monospace",color:"#c4b5fd" }}>{maint}<span style={{ fontSize:10,color:"#666" }}>kcal</span></div>
          </div>
          <div style={{ background:"rgba(251,146,60,0.07)",borderRadius:10,padding:"10px 12px" }}>
            <div style={{ fontSize:10,color:"#888",marginBottom:4 }}>Eaten</div>
            <div style={{ fontSize:17,fontWeight:700,fontFamily:"'Space Mono',monospace",color:"#fb923c" }}>{eaten}<span style={{ fontSize:10,color:"#666" }}>kcal</span></div>
          </div>
          {woBurn > 0 && (
            <div style={{ background:"rgba(52,211,153,0.07)",borderRadius:10,padding:"10px 12px" }}>
              <div style={{ fontSize:10,color:"#888",marginBottom:4 }}>Workout</div>
              <div style={{ fontSize:17,fontWeight:700,fontFamily:"'Space Mono',monospace",color:"#34d399" }}>+{woBurn}<span style={{ fontSize:10,color:"#666" }}>kcal</span></div>
            </div>
          )}
        </div>
        <div style={{ height:4,borderRadius:2,background:"rgba(255,255,255,0.07)",marginBottom:10,overflow:"hidden" }}>
          <div style={{ height:"100%",width:`${pct}%`,borderRadius:2,background:isDeficit?"#4ade80":"#f87171",transition:"width 0.4s" }}/>
        </div>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <span style={{ fontSize:11,color:"#666" }}>{pct}% of total burn · {totalBurn} kcal maintenance + workout</span>
          <span style={{ fontFamily:"'Space Mono',monospace",fontWeight:700,fontSize:14,color:isDeficit?"#4ade80":"#f87171" }}>
            {isDeficit?"↓":"↑"}{Math.abs(net)}kcal {isDeficit?"deficit":"surplus"}
          </span>
        </div>
      </div>
    );
  })()}

  {/* ── Fitness Goals Box ── */}
  {(()=>{
    const navyBF = calcNavyBF(fitnessGoals.neck, fitnessGoals.waist, fitnessGoals.height);
    const composition = getCurrentBodyComposition(fitnessGoals, weightLog);
    const baseline = composition.baseline;
    const displayBF = composition.bodyFat ?? fitnessGoals.bodyFatCurrent;
    const lbm = composition.leanMass;
    const bfSource = baseline?.source === "navy" ? "Navy + weight adjusted" : baseline?.source === "manual" ? "Manual + weight adjusted" : "Boer estimate";
    const latestWo  = [...workouts].sort((a,b)=>b.date.localeCompare(a.date))[0];
    const allCards = [
      { key:"bodyFat", label:"Body Fat",    current:displayBF,                  target:fitnessGoals.bodyFatTarget, unit:"%",      color:"#818cf8", lower:true,  note: bfSource },
      { key:"weight",  label:"Weight",      current:composition.weight ?? fitnessGoals.weightCurrent,  target:fitnessGoals.weightTarget,  unit:"kg",     color:"#a5b4fc", lower:true,  note:"LBM: " + (lbm != null ? lbm.toFixed(1) : "—") + "kg" },
      { key:"muscle",  label:"Muscle Mass", current:fitnessGoals.muscleCurrent,  target:fitnessGoals.muscleTarget,  unit:"%",      color:"#c7d2fe", lower:false, note:"Manual / scale metric" },
      { key:"run",     label:"Run Target",  current:latestWo?.runPace||0,        target:fitnessGoals.runPaceTarget, unit:"min/km", color:"#e0e7ff", lower:true,  dist:fitnessGoals.runDistTarget, isRun:true },
    ].filter(g=>visibleGoalCards[g.key]);
    return (
      <div style={{ background:"linear-gradient(145deg,#0d0b1f 0%,#0a0818 100%)",border:"1px solid rgba(129,140,248,0.22)",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.4),inset 0 1px 0 rgba(129,140,248,0.08)" }}>
        <div style={{ padding:"13px 20px",borderBottom:"1px solid rgba(129,140,248,0.1)",background:"rgba(129,140,248,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10 }}>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <span style={{ fontSize:14 }}>🎯</span>
            <span style={{ fontWeight:600,fontSize:14,color:"#e2e8f0" }}>Fitness Goals</span>
            <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#818cf8",background:"rgba(129,140,248,0.12)",padding:"2px 7px",borderRadius:4 }}>BODY COMPOSITION</span>
          </div>
          <div style={{ display:"flex",gap:6,alignItems:"center",flexWrap:"wrap" }}>
            {[["bodyFat","BF%","#818cf8"],["weight","Weight","#a5b4fc"],["muscle","Muscle","#c7d2fe"],["run","Run","#e0e7ff"]].map(([k,label,c])=>(
              <button key={k} onClick={()=>setVisibleGoalCards(v=>({...v,[k]:!v[k]}))} style={{
                ...btn, fontSize:10, padding:"3px 9px", fontFamily:"'Space Mono',monospace",
                background: visibleGoalCards[k]?"rgba(129,140,248,0.15)":"rgba(255,255,255,0.03)",
                color:      visibleGoalCards[k]?c:"#333",
                border:     "1px solid " + (visibleGoalCards[k]?"rgba(129,140,248,0.35)":"rgba(255,255,255,0.07)"),
              }}>{label}</button>
            ))}
            <button onClick={()=>setEditFitnessGoals(g=>!g)} style={{ ...btn,fontSize:11,padding:"5px 12px",background:"rgba(129,140,248,0.1)",color:"#818cf8",border:"1px solid rgba(129,140,248,0.25)" }}>
              {editFitnessGoals?"✕ Close":"⚙ Set Goals"}
            </button>
          </div>
        </div>
        {editFitnessGoals && (
          <div style={{ padding:"16px 20px",borderBottom:"1px solid rgba(129,140,248,0.08)",background:"rgba(129,140,248,0.03)" }}>
            <div style={{ fontSize:11,color:"#818cf8",background:"rgba(129,140,248,0.08)",border:"1px solid rgba(129,140,248,0.15)",borderRadius:8,padding:"8px 12px",marginBottom:12 }}>
              Enter neck + waist at navel level to calculate Navy Method body fat. That reading anchors lean mass, then weight changes estimate body-fat movement.
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:10 }}>
              {[
                ["weightCurrent","Current Weight","kg","#a5b4fc"],
                ["weightTarget","Target Weight","kg","#a5b4fc"],
                ["height","Height","cm","#a5b4fc"],
                ["age","Age","yrs","#a5b4fc"],
                ["neck","Neck","cm","#818cf8"],
                ["waist","Waist at navel","cm","#818cf8"],
                ["bodyFatTarget","BF% Target","%","#818cf8"],
                ["muscleTarget","Muscle Target","%","#c7d2fe"],
                ["runDistTarget","Run Distance","km","#e0e7ff"],
                ["runPaceTarget","Run Pace","min/km","#e0e7ff"],
              ].map(([k,label,unit,c])=>(
                <div key={k}>
                  <label style={{ display:"block",fontSize:10,color:c,marginBottom:4 }}>{label} <span style={{color:"#8878aa"}}>({unit})</span></label>
                  <input type="number" step="0.1" min="0" style={{...inp,borderColor:"rgba(129,140,248,0.2)"}} value={fitnessGoals[k]||""}
                    onChange={e=>{ const v=parseFloat(e.target.value); if(!isNaN(v)&&v>=0) saveFitnessGoals({...fitnessGoals,[k]:v}); }} />
                </div>
              ))}
            </div>
            {fitnessGoals.neck>0 && fitnessGoals.waist>0 && (
              <div style={{ marginTop:10,padding:"8px 12px",background:"rgba(129,140,248,0.08)",borderRadius:8,fontSize:11,color:"#818cf8",fontFamily:"'Space Mono',monospace" }}>
                Navy BF: {navyBF || "—"}% ·
                Estimated BF now: {displayBF ?? "—"}% ·
                LBM anchor: {baseline?.leanMass != null ? baseline.leanMass.toFixed(1) : "—"}kg
              </div>
            )}
          </div>
        )}
        <div style={{ padding:"16px 20px" }}>
          {allCards.length===0 ? (
            <div style={{ textAlign:"center",padding:"16px 0",color:"#8878aa",fontSize:13 }}>Toggle a metric above to show it</div>
          ) : (
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))",gap:10 }}>
              {allCards.map(g=>{
                const diff = g.lower ? g.current-g.target : g.target-g.current;
                const achieved = diff<=0;
                return (
                  <div key={g.key} style={{ background:"rgba(129,140,248,0.05)",border:"1px solid " + (achieved?"rgba(99,102,241,0.4)":"rgba(129,140,248,0.1)"),borderRadius:12,padding:"13px 15px" }}>
                    <div style={{ display:"flex",justifyContent:"space-between",marginBottom:4 }}>
                      <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#9988bb",textTransform:"uppercase",letterSpacing:1 }}>{g.label}</span>
                      {achieved
                        ? <span style={{ fontSize:9,color:"#4ade80",fontFamily:"'Space Mono',monospace" }}>✓ GOAL</span>
                        : <span style={{ fontSize:9,color:"#8878aa",fontFamily:"'Space Mono',monospace" }}>→ {g.target}{g.unit}</span>}
                    </div>
                    <div style={{ display:"flex",alignItems:"baseline",gap:4,marginBottom:8 }}>
                      <span style={{ fontSize:24,fontWeight:700,fontFamily:"'Space Mono',monospace",color:g.color }}>
                        {g.isRun&&g.dist ? g.dist+"km" : g.current>0 ? g.current.toFixed(g.unit==="kg"?2:1) : "—"}
                      </span>
                      <span style={{ fontSize:11,color:"#9988bb" }}>{g.isRun ? "@ "+g.target+"min/km" : g.unit}</span>
                    </div>
                    <div style={{ background:"rgba(129,140,248,0.1)",borderRadius:4,height:4,overflow:"hidden",marginBottom:5 }}>
                      <div style={{ width:(achieved?100:30)+"%",height:"100%",background:achieved?"#4ade80":g.color,borderRadius:4,transition:"width 0.6s" }}/>
                    </div>
                    <div style={{ display:"flex",justifyContent:"space-between" }}>
                      <span style={{ fontSize:9,color:"#8878aa",fontStyle:"italic" }}>{g.note}</span>
                      <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:achieved?"#4ade80":"#9988bb" }}>
                        {achieved?"Done!": isNaN(diff)?"—" : (g.lower?"↓":"↑")+Math.abs(diff).toFixed(g.unit==="kg"?2:1)+g.unit}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  })()}

  {/* ── Today's Nutrition Box ── */}
  <div style={{ background:"linear-gradient(145deg,#160c02 0%,#110900 100%)",border:"1px solid rgba(251,146,60,0.22)",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.4),inset 0 1px 0 rgba(251,146,60,0.08)" }}>
    <div style={{ padding:"13px 20px",borderBottom:"1px solid rgba(251,146,60,0.1)",background:"rgba(251,146,60,0.05)",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10 }}>
      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
        <span style={{ fontSize:14 }}>🍽</span>
        <span style={{ fontWeight:600,fontSize:14,color:"#e2e8f0" }}>Today's Nutrition</span>
        <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#fb923c",background:"rgba(251,146,60,0.12)",padding:"2px 7px",borderRadius:4 }}>DAILY TARGETS</span>
      </div>
      <button onClick={()=>setEditMacroGoals(g=>!g)} style={{ ...btn,fontSize:11,padding:"5px 12px",background:"rgba(251,146,60,0.1)",color:"#fb923c",border:"1px solid rgba(251,146,60,0.25)" }}>
        {editMacroGoals?"✕ Close":"⚙ Set Goals"}
      </button>
    </div>
    {editMacroGoals && (
      <div style={{ padding:"16px 20px",borderBottom:"1px solid rgba(251,146,60,0.08)",background:"rgba(251,146,60,0.03)",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10 }}>
        {[["calories","kcal","#fb923c"],["protein","g","#60a5fa"],["carbs","g","#4ade80"],["fat","g","#fbbf24"],["fiber","g","#c084fc"]].map(([k,u,c])=>(
          <div key={k}>
            <label style={{ display:"block",color:c,fontSize:10,marginBottom:4 }}>{k} <span style={{color:"#e89050"}}>({u})</span></label>
            <input type="number" style={{...inp,borderColor:"rgba(251,146,60,0.2)"}} value={macroGoals[k]}
              onChange={e=>saveMacroGoals({...macroGoals,[k]:parseFloat(e.target.value)||0})} />
          </div>
        ))}
      </div>
    )}
    <div style={{ padding:"16px 20px",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:10 }}>
      {[["calories","kcal","#fb923c"],["protein","g","#60a5fa"],["carbs","g","#4ade80"],["fat","g","#fbbf24"],["fiber","g","#c084fc"]].map(([k,u,c])=>{
        const val  = todayTotals?.[k]??0;
        const goal = macroGoals[k];
        const pct  = Math.min((val/goal)*100,100);
        return (
          <div key={k} style={{ background:"rgba(251,146,60,0.04)",border:"1px solid rgba(251,146,60,0.1)",borderRadius:12,padding:"12px 14px" }}>
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:7 }}>
              <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#ffb87a",textTransform:"uppercase",letterSpacing:1 }}>{k}</span>
              <span style={{ fontSize:10,color:"#e89050" }}>/{goal}{u}</span>
            </div>
            <div style={{ fontSize:22,fontWeight:700,fontFamily:"'Space Mono',monospace",color:c,marginBottom:8 }}>{Math.round(val*10)/10}<span style={{ fontSize:10,fontWeight:400,marginLeft:2,color:"#d08848" }}>{u}</span></div>
            <div style={{ background:"rgba(251,146,60,0.1)",borderRadius:4,height:4,overflow:"hidden" }}>
              <div style={{ width:pct+"%",height:"100%",background:c,borderRadius:4,transition:"width 0.5s" }}/>
            </div>
            <div style={{ fontSize:10,color:pct>=100?"#4ade80":"#ffb87a",marginTop:5,fontFamily:"'Space Mono',monospace" }}>
              {pct>=100?"✓ Hit":"↑ "+Math.round(goal-val)+u+" left"}
            </div>
          </div>
        );
      })}
    </div>
  </div>

  {/* ── Today's Workout ── */}
  {(()=>{
    const todayWos = workouts.filter(w=>w.date===today);
    return (
      <div style={{ background:"linear-gradient(145deg,#061a14 0%,#04110e 100%)",border:"1px solid rgba(52,211,153,0.25)",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(52,211,153,0.1)" }}>
        <div style={{ padding:"13px 20px",borderBottom:"1px solid rgba(52,211,153,0.12)",display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(52,211,153,0.06)" }}>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <span style={{ fontSize:14 }}>⌚</span>
            <span style={{ fontWeight:600,fontSize:14,color:"#e2e8f0" }}>Today's Activity</span>
            {todayWos.length>0 && <span style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#34d399",background:"rgba(52,211,153,0.12)",padding:"2px 7px",borderRadius:4 }}>{todayWos.length} SESSION{todayWos.length>1?"S":""}</span>}
          </div>
          <button onClick={()=>setView("Fitness")} style={{ ...btn,fontSize:11,padding:"5px 12px",
            background: todayWos.length?"rgba(52,211,153,0.12)":"rgba(52,211,153,0.06)",
            color: todayWos.length?"#34d399":"#8dddb5",
            border:"1px solid " + (todayWos.length?"rgba(52,211,153,0.3)":"rgba(52,211,153,0.1)") }}>
            {todayWos.length ? "+ Add Session" : "Log Workout →"}
          </button>
        </div>
        {todayWos.length===0 ? (
          <div style={{ padding:"28px 20px",display:"flex",alignItems:"center",gap:16 }}>
            <div style={{ width:44,height:44,borderRadius:12,background:"rgba(52,211,153,0.07)",border:"1px solid rgba(52,211,153,0.12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0 }}>⌚</div>
            <div>
              <div style={{ fontSize:13,color:"#8dddb5",marginBottom:4 }}>No activity logged today</div>
              <div style={{ fontSize:11,color:"#6ec89a" }}>Go to Fitness to log a workout — steps, calories, distance and more.</div>
            </div>
          </div>
        ) : (
          <div style={{ padding:"16px 20px",display:"flex",flexDirection:"column",gap:14 }}>
            {todayWos.map((wo,wi)=>(
              <div key={wo.id||wi}>
                {todayWos.length>1 && (
                  <div style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:"#34d399",textTransform:"uppercase",letterSpacing:1,marginBottom:8 }}>
                    Session {wi+1} · {wo.workoutType}
                  </div>
                )}
                <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10 }}>
                  {[
                    ["👟","Steps",      wo.steps>0          ? wo.steps.toLocaleString() : null, "#22d3ee"],
                    ["🔥","Cal Burned", wo.caloriesBurned>0 ? wo.caloriesBurned+"kcal"  : null, "#fb923c"],
                    ["❤️","Avg HR",     wo.heartRateAvg>0   ? wo.heartRateAvg+"bpm"     : null, "#f87171"],
                    ["⏱","Active",     wo.activeMinutes>0  ? wo.activeMinutes+"min"    : null, "#818cf8"],
                    ["📍","Distance",   wo.distance>0       ? wo.distance+"km"          : null, "#34d399"],
                    ["🏃","Pace",       wo.runPace>0        ? wo.runPace+"min/km"       : null, "#c084fc"],
                  ].filter(([,,v])=>v!==null).map(([icon,label,val,c])=>(
                    <div key={label} style={{ background:"rgba(52,211,153,0.05)",border:"1px solid rgba(52,211,153,0.1)",borderRadius:10,padding:"10px 13px" }}>
                      <div style={{ fontSize:10,color:"#8dddb5",marginBottom:5,display:"flex",alignItems:"center",gap:4 }}>
                        <span>{icon}</span><span style={{ fontFamily:"'Space Mono',monospace",textTransform:"uppercase",letterSpacing:0.8 }}>{label}</span>
                      </div>
                      <div style={{ fontSize:17,fontWeight:700,fontFamily:"'Space Mono',monospace",color:c }}>{val}</div>
                    </div>
                  ))}
                </div>
                {wo.notes && <div style={{ marginTop:8,padding:"6px 10px",background:"rgba(52,211,153,0.05)",borderRadius:8,fontSize:12,color:"#8dddb5",fontStyle:"italic" }}>📝 {wo.notes}</div>}
                {todayWos.length>1 && wi<todayWos.length-1 && <div style={{ marginTop:14,borderBottom:"1px solid rgba(52,211,153,0.08)" }}/>}
              </div>
            ))}
            {todayWos.length>1 && (
              <div style={{ padding:"8px 12px",background:"rgba(52,211,153,0.06)",borderRadius:8,fontSize:11,fontFamily:"'Space Mono',monospace",color:"#34d399" }}>
                TOTAL · {todayWos.reduce((s,w)=>s+(w.caloriesBurned||0),0)}kcal burned · {todayWos.reduce((s,w)=>s+(w.activeMinutes||0),0)}min active
              </div>
            )}
          </div>
        )}
      </div>
    );
  })()}


  {/* ── Micro Meal Log ── */}
  {(()=>{
    const todayLog = logs.find(l=>l.date===today);
    const loggedCount = MEALS.filter(m=>todayLog?.meals?.[m]?.logged).length;
    return (
      <div style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,overflow:"hidden" }}>
        <div style={{ padding:"14px 20px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
          <div>
            <span style={{ fontWeight:600,fontSize:14 }}>Today's Meals</span>
            <span style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#999",marginLeft:10 }}>{loggedCount}/{MEALS.length} logged</span>
          </div>
          <button onClick={()=>setView("Meal Log")} style={{ ...btn,fontSize:11,padding:"5px 12px",background:"rgba(255,255,255,0.05)",color:"#aaa",border:"1px solid rgba(255,255,255,0.09)" }}>
            Edit details →
          </button>
        </div>
        {MEALS.map((meal,mi)=>{
          const logged = todayLog?.meals?.[meal];
          const mc = logged?.macros;
          const isLast = mi === MEALS.length-1;
          return (
            <div key={meal} style={{ padding:"13px 20px", borderBottom:isLast?"none":"1px solid rgba(255,255,255,0.04)", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ width:9,height:9,borderRadius:"50%",flexShrink:0,
                background: logged?.logged ? "#22c55e" : "rgba(255,255,255,0.1)",
                boxShadow: logged?.logged ? "0 0 6px rgba(34,197,94,0.5)" : "none",
              }}/>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontSize:13,fontWeight:600,marginBottom:2 }}>{meal}</div>
                {logged?.logged ? (
                  <div style={{ display:"flex",gap:8,flexWrap:"wrap",alignItems:"center" }}>
                    <span style={{ fontSize:11,fontFamily:"'Space Mono',monospace",color:"#f97316" }}>{mc?.calories??0} kcal</span>
                    {[["P",mc?.protein,"#3b82f6"],["C",mc?.carbs,"#22c55e"],["F",mc?.fat,"#f59e0b"],["Fi",mc?.fiber,"#a78bfa"]].map(([l,v,c])=>(
                      <span key={l} style={{ fontSize:10,fontFamily:"'Space Mono',monospace",color:c }}>{l} {v??0}g</span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize:11,color:"#888" }}>Not logged yet</div>
                )}
              </div>
              {logged?.logged ? (
                <button onClick={()=>{ setView("Meal Log"); openMeal(meal); }} style={{ ...btn,fontSize:11,padding:"5px 12px",background:"rgba(34,197,94,0.1)",color:"#22c55e",border:"1px solid rgba(34,197,94,0.2)",flexShrink:0 }}>
                  ✓ Edit
                </button>
              ) : (
                <button onClick={()=>{ setView("Meal Log"); openMeal(meal); }} style={{ ...btn,fontSize:11,padding:"5px 14px",background:"linear-gradient(135deg,rgba(249,115,22,0.2),rgba(236,72,153,0.2))",color:"#f97316",border:"1px solid rgba(249,115,22,0.25)",flexShrink:0 }}>
                  Log
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  })()}

  {/* ── Trend Chart ── */}
  <div style={{ background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,padding:20 }}>
    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:18 }}>
      <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
        {Object.entries(mColors).map(([k,c])=>(
          <button key={k} onClick={()=>setChartMetric(k)} style={{ ...btn,padding:"5px 12px",fontSize:11,background:chartMetric===k?c:"rgba(255,255,255,0.05)",color:chartMetric===k?"#fff":"#666" }}>
            {k.charAt(0).toUpperCase()+k.slice(1)}
          </button>
        ))}
      </div>
      <div style={{ display:"flex",gap:5 }}>
        {[["week","7D"],["month","30D"],["3months","90D"],["all","All"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTimeRange(k)} style={{ ...btn,padding:"4px 10px",fontSize:11,fontFamily:"'Space Mono',monospace",border:"1px solid rgba(255,255,255,0.09)",background:timeRange===k?"rgba(255,255,255,0.09)":"transparent",color:timeRange===k?"#fff":"#555" }}>{l}</button>
        ))}
      </div>
    </div>
    {chartLogs.length>1 ? (
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartLogs}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)"/>
          <XAxis dataKey="date" tick={{fill:"#444",fontSize:11}} axisLine={false} tickLine={false}/>
          <YAxis tick={{fill:"#444",fontSize:11}} axisLine={false} tickLine={false} width={40}/>
          <Tooltip contentStyle={{background:"#121220",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,color:"#fff"}} labelStyle={{color:"#888",fontSize:12}}/>
          <Line type="monotone" dataKey={chartMetric} stroke={mColors[chartMetric]} strokeWidth={2.5} dot={{r:4,fill:mColors[chartMetric],strokeWidth:0}} activeDot={{r:6}}/>
        </LineChart>
      </ResponsiveContainer>
    ) : (
      <div style={{ height:200,display:"flex",alignItems:"center",justifyContent:"center",color:"#888",fontSize:14 }}>Log meals on multiple days to see your trend</div>
    )}
  </div>
</div>
  );
}
